import * as Yup from 'yup';
import dayjs from 'dayjs';
import { dateFormat } from '@shared/constants/formats';
import { Nullable } from '../../../../../../../typings/global';
import { ICoApplicant } from './components/CoApplicant/validation';

// eslint-disable-next-line @typescript-eslint/no-namespace
export declare namespace NContact {
  export type Address = Nullable<{
    isCurrent: boolean;
    includeInDti: boolean;
    monthsAtAddress: number;
    streetLine1: string;
    streetLine2?: string;
    unit: string;
    city: string;
    state: string;
    zip: string;
    type: string;
    county: string;
    cdfitm?: boolean;
  }>;

  export type Employment = Nullable<{
    monthsAtJob: number;
    yrs: number;
    mths: number;
    jobTitle: string;
    employmentType: string;
    companyName: string;
    monthlyIncomeAmount: number;
    isCurrent: boolean;
    isPrimary: boolean;
    streetLine1: string;
    streetLine2: string;
    startDate: string;
    city: string;
    state: string;
    zip: string;
    type: string;
    county: string;
  }>;

  export interface OtherIncome {
    monthlyAmount: number;
    incomeType: string;
    isCurrent: boolean;
    monthsAtJob: number;
  }

  export type Reference = Nullable<{
    fullName: string;
    phoneNumber: string;
    emailAddress: string;
    streetLine1: string;
    streetLine2: string;
    city: string;
    state: string;
    zip: string;
    type: string;
    county: string;
    relationshipType: string;
  }>;

  export interface CoApplicant {
    id: string;
    numericId: number;
    firstName: string;
    lastName: string;
    cellPhone: string;
    workPhone?: string;
    homePhone?: string;
    email: string;
    driverLicenseNumber: string;
    driverLicenseState: string;
    relationshipType: string;
    isDroppedContact: boolean;
  }

  export interface Loan {
    loanAmount: number;
    term: number;
    rate: number;
    monthlyPayment: number;
    perDiem: number;
    nextPaymentDate: string;
  }

  export interface Zip {
    code: string;
  }

  export interface Address2 {
    streetLine1: string;
    streetLine2: string;
    city: string;
    state: string;
    zip: Zip;
    type: string;
    county: string;
  }

  export interface CurrentProduct {
    refundAmount: number;
    productType: string;
  }

  export interface AssetLienHolder {
    name: string;
    lienHolderId: string;
    remainingLoanAmount: number;
    remainingTerm: number;
    goodDate: string;
    loan: Loan;
    address: Address2;
    currentProducts: CurrentProduct[];
    accountNumber: string;
    phoneNumber: string;
  }

  export interface AssetOption {
    description: string;
    valuationRetailAmount: number;
    valuationWholesaleAmount: number;
    assetValuationProviderOptionId: string;
    isSelected: boolean;
  }

  export interface BookOut {
    createdDate: string;
  }

  export interface AssetValuation {
    type: string;
    providerVehicleId: number;
    baseRetailAmount: number;
    baseTradeInAmount: number;
    baseWholesaleAmount: number;
    baseFinanceExpressAmount: number;
    valuationMileage: string;
    mileageAdjustedRetailAmount: number;
    mileageAdjustedTradeInAmount: number;
    mileageAdjustedWholesaleAmount: number;
    valuationRegion: string;
    valuationZip: string;
    valuationDate: string;
    assetOptions: AssetOption[];
    optionAdjustedRetailAmount: number;
    optionAdjustedTradeInAmount: number;
    optionAdjustedWholesaleAmount: number;
    bookOut: BookOut;
  }

  export interface AssetRegistration {
    primaryOwner: string;
    secondaryOwner: string;
    number: string;
    state: string;
    expirationDate: string;
  }

  export interface AssetInsurance {
    company: string;
    phoneNumber: string;
    policyNumber: string;
    effectiveDate: string;
    expirationDate: string;
  }

  export interface UpdatedBy {
    id: string;
    email: string;
    name: string;
  }

  export interface ContactAsset {
    id: string;
    assetType: string;
    vin: string;
    year: number;
    make: string;
    model: string;
    kbbTrim: string;
    nadaTrim: string;
    bodyStyle: string;
    mileage: string;
    assetCondition: string;
    color: string;
    assetUsage: string;
    msrp: number;
    assetLienHolder: AssetLienHolder;
    assetValuations: AssetValuation[];
    assetRegistration: AssetRegistration;
    assetInsurance: AssetInsurance;
    createdBy: UpdatedBy;
    createdDate: string;
    updatedBy: UpdatedBy;
    updatedDate: string;
    isDeleted: boolean;
    contactId: string;
  }

  export interface Notification {
    message: string;
    key: string;
    data: string;
    color: string;
  }

  export interface Entity {
    id?: string;
    numericId?: number;
    msg?: string;
    firstName: string;
    middleInitial: string;
    lastName: string;
    nickName: string;
    suffix: string;
    maidenName: string;
    ssn: string;
    dateOfBirth: string;
    homePhone: string;
    workPhone: string;
    workPhoneExtension: string;
    cellPhone: string;
    email: string;
    workEmail: string;
    militaryStatus: string;
    maritalStatus: string;
    driverLicenseNumber: string;
    driverLicenseExpireDate: string;
    driverLicenseIssuesOnDate: string;
    driverLicenseState: string;
    isMilitaryLendingActAvailable: boolean;
    residencyStatusType: string;
    isDeceased: boolean;
    isSpanish: boolean;
    isOptOut: boolean;
    oneCreditPull: boolean;
    referralEmail: string;
    referralContactName: string;
    referralContactId: string;
    addresses: Address[];
    employments: Employment[];
    otherIncomes: OtherIncome[];
    references: Reference[];
    coApplicants: CoApplicant[];
    assetResponse: ContactAsset | null;
    notifications: Notification[];
    additionalInfo: { [key: string]: string };
    isMLAFlagValueChanged?: boolean;
    isOneCreditPullValueChanged?: boolean;
    purlPin?: string | null;
  }
}

export const commonDriverLicenseNumberValidation = Yup.string()
  .nullable()
  .max(20, 'Maximum 20 symbols')
  .test('is-alpha-numeric', 'Only alpha-numeric characters are allowed', (value) => {
    if (!value) return true;
    return /^[a-zA-Z0-9]*$/.test(value);
  })
  .test('has-numeric', 'At least one numeric value is required', (value) => {
    if (!value) return true;
    return /[0-9]/.test(value);
  });

export const contactValidationSchema = Yup.object().shape({
  firstName: Yup.string().nullable().max(128).required(),
  lastName: Yup.string().nullable().max(128).required(),
  middleInitial: Yup.string().nullable().max(128),
  nickName: Yup.string().nullable().max(128),
  suffix: Yup.string().nullable(),
  dateOfBirth: Yup.date()
    .min(new Date(1900, 1, 1), 'DOB must be later than 01/01/1900')
    .max(new Date(), `DOB must be earlier than ${dayjs(new Date()).format(dateFormat)}`)
    .typeError('DOB is invalid')
    .nullable(),
  ssn: Yup.string()
    .matches(/^\d{9}$/, 'SSN must be 9 digits')
    .nullable(),
  maritalStatus: Yup.string().nullable(),
  residencyStatusType: Yup.string().nullable(),
  maidenName: Yup.string().nullable().max(128, 'Maximum 128 symbols'),
  militaryStatus: Yup.string().nullable(),
  referralEmail: Yup.string().email().nullable(),
  referralContactName: Yup.string().nullable(),
  email: Yup.string().required().email().nullable().max(128, 'Email field Address field should be max 128 symbols'),
  workEmail: Yup.string().nullable().email(),
  homePhone: Yup.string().phone().nullable(),
  workPhone: Yup.string().phone().nullable(),
  cellPhone: Yup.string().phone().nullable(),
  driverLicenseNumber: commonDriverLicenseNumberValidation,
  driverLicenseState: Yup.string().nullable(),
  driverLicenseIssuesOnDate: Yup.date().nullable(),
  driverLicenseExpireDate: Yup.date().typeError('Expiry Date is invalid').nullable().min(Yup.ref('driverLicenseIssuesOnDate'), 'The Expiry date must be after the Issue date'),
  isDeceased: Yup.boolean(),
  isSpanish: Yup.boolean(),
});

export const coApplicantValidationSchema = contactValidationSchema.shape({
  relationshipType: Yup.string().nullable(),
});

export class ContactModel implements ICoApplicant {
  id: string | null = null;
  numericId: number | null = null;
  firstName: string | null = null;
  middleInitial: string | null = null;
  nickName: string | null = null;
  lastName: string | null = null;
  suffix: string | null = null;
  maidenName: string | null = null;
  ssn: string | null = null;
  dateOfBirth: string | null = null;
  homePhone: string | null = null;
  workPhone: string | null = null;
  workPhoneExtension: string | null = null;
  cellPhone: string | null = null;
  email: string | null = null;
  workEmail: string | null = null;
  militaryStatus: string | null = null;
  maritalStatus: string | null = null;
  driverLicenseNumber: string | null = null;
  driverLicenseExpireDate: string | null = null;
  driverLicenseIssuesOnDate: string | null = null;
  driverLicenseState: string | null = null;
  isMilitaryLendingActAvailable = false;
  residencyStatusType: string | null = null;
  isDeceased = false;
  isSpanish = false;
  isOptOut = false;
  oneCreditPull = false;
  referralEmail: string | null = null;
  referralContactName: string | null = null;
  referralContactId: string | null = null;
  addresses: NContact.Address[] = [];
  employments: NContact.Employment[] = [];
  otherIncomes: NContact.OtherIncome[] = [];
  references: NContact.Reference[] = [];
  coApplicants: NContact.CoApplicant[] = [];
  assetResponse: NContact.ContactAsset | null = null;
  notifications: NContact.Notification[] = [];
  relationshipType: string | null = 'None';
  coApplicantId?: string | null = null;
  additionalInfo: { [key: string]: string } = {};
  isMLAFlagValueChanged?: boolean;
  isOneCreditPullValueChanged?: boolean;
  purlPin?: string | null;

  constructor(data?: Partial<ICoApplicant>) {
    if (data) {
      this.id = data.id || null;
      this.numericId = data.numericId || null;
      this.firstName = data.firstName || null;
      this.middleInitial = data.middleInitial || null;
      this.nickName = data.nickName || null;
      this.lastName = data.lastName || null;
      this.suffix = data.suffix || null;
      this.maidenName = data.maidenName || null;
      this.ssn = data.ssn || null;
      this.dateOfBirth = data.dateOfBirth || null;
      this.homePhone = data.homePhone || null;
      this.workPhone = data.workPhone || null;
      this.workPhoneExtension = data.workPhoneExtension || null;
      this.cellPhone = data.cellPhone || null;
      this.email = data.email || null;
      this.workEmail = data.workEmail || null;
      this.militaryStatus = data.militaryStatus || null;
      this.maritalStatus = data.maritalStatus || null;
      this.driverLicenseNumber = data.driverLicenseNumber || null;
      this.driverLicenseExpireDate = data.driverLicenseExpireDate || null;
      this.driverLicenseIssuesOnDate = data.driverLicenseIssuesOnDate || null;
      this.driverLicenseState = data.driverLicenseState || null;
      this.isMilitaryLendingActAvailable = data.isMilitaryLendingActAvailable || false;
      this.isMLAFlagValueChanged = data?.isMLAFlagValueChanged || false;
      this.isOneCreditPullValueChanged = data?.isOneCreditPullValueChanged || false;
      this.residencyStatusType = data.residencyStatusType || null;
      this.isDeceased = data.isDeceased || false;
      this.isSpanish = data.isSpanish || false;
      this.isOptOut = data.isOptOut || false;
      this.oneCreditPull = data.oneCreditPull || false;
      this.referralEmail = data.referralEmail || null;
      this.referralContactName = data.referralContactName || null;
      this.referralContactId = data.referralContactId || null;
      this.addresses = data.addresses || [];
      this.employments = data.employments || [];
      this.otherIncomes = data.otherIncomes || [];
      this.references = data.references || [];
      this.coApplicants = data.coApplicants || [];
      this.assetResponse = data.assetResponse || null;
      this.notifications = data.notifications || [];
      this.relationshipType = data.relationshipType || 'None';
      this.coApplicantId = data.coApplicantId || null;
      this.purlPin = data.purlPin || null;
    }
  }
}
